import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  ElementRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectionStrategy,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { isEqual } from 'lodash';
import { combineLatest, fromEvent, of, ReplaySubject, Subject } from 'rxjs';
import { delay, distinctUntilChanged, filter, pairwise, skip, switchMap, takeUntil, tap } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable, selectors as coreSelectors } from 'app/core/state';
import SocialStateMachine from 'app/modules/social/social-state-machine/social-state-machine';
import { ModalErrorComponent, ModalErrorData } from 'app/shared/modal-error/modal-error.component';
import { SnackBarComponent, SnackBarData, SnackBarType } from 'app/shared/snackbar/snackbar.component';
import assertDefined from 'app/utils/assert-defined';
import { HelpCenterUrls } from 'app/utils/help-center';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import Playlist from 'entity/Playlist';
import Social from 'entity/Social';
import { PRESENT, ANYONE_CAN_PRESENT, ANYONE_CAN_SHARE, AI_SUMMARIES } from 'entity/SocialSettings';
import User from 'entity/User';
import { SocialEventType, ScreenShareStatus, InteractionMode } from 'messages/websocket.messages';

import { INITIAL_MUSIC_VOLUME } from '../../constants';
import { HelpModalComponent } from '../../help-modal/help-modal.component';
import { MultimediaType } from '../../multimedia-dropdown/multimedia-dropdown.types';
import { ActionMenuService } from '../../services/action-menu.service';
import { ChatService } from '../../services/chat.service';
import { DebuggerService } from '../../services/debugger.service';
import { DeviceService } from '../../services/device/device.service';
import { DevicePermissionService } from '../../services/device-permission/device-permission.service';
import { DefaultEmojis, EmoteService, Emoticon } from '../../services/emote/emote.service';
import { MultimediaRoomService } from '../../services/multimedia-room.service';
import { MultimediaUserService } from '../../services/multimedia-user.service';
import { PlaylistService } from '../../services/playlist.service';
import { PresentationRecorderService } from '../../services/presentation/presentation-recorder.service';
import { MediaEmbedType, PresentationService } from '../../services/presentation/presentation.service';
import { SceneService } from '../../services/scene.service';
import { SearchParticipantsService } from '../../services/search-participants/search-participants.service';
import { ShareScreenParticipantService } from '../../services/share-screen/share-screen-participant.service';
import { ShareScreenService } from '../../services/share-screen/share-screen.service';
import { GroupShareScreenRecorderService } from '../../services/share-screen-group/group-share-screen-recorder.service';
import { SideSectionService } from '../../services/side-section.service';
import { SideContainerType } from '../../social-menu/enums/SideContainerType';
import { CHAT_SUB_MENU } from '../../social-menu/social-menu.types';
import SocialStateHelper from '../../social-state-machine/social-state-helper';
import { InteractionModePresets } from '../../social-state-machine/social-state.constants';
import SoundControl from '../../sound-control';
import {
  ExtendedPresenter,
  PopOutState,
  socialAppActions,
  socialAppSelectors,
  SpecialAccessGroupModalMode,
  SpecialAccessGroupModalState,
} from '../../state/app';
import { getPresenterIds } from '../../state/app/selectors';
import { mediaSelectors } from '../../state/media';
import { CAMERA_UNREADABLE_HELP_TEXT, CAMERA_UNREADABLE_TEXT_IN_ROOM, CAMERA_UNREADABLE_TITLE } from '../../strings';
import { PresentationEmbedModalComponent } from '../presentation-embed-modal/presentation-embed-modal.component';

import { ClosedCaptionsService } from './../../services/closed-captions/closed-captions.service';
// import { SpeechToTextService } from './../../services/speech-to-text/speech-to-text.service';
import { PresentationMenuDropdownComponent } from './presentation-menu-dropdown/presentation-menu-dropdown.component';
import { PresentationMenuType } from './presentation-menu-dropdown/presentation-menu-dropdown.types';
import { Player } from '../../social-pixi-canvas/player';
import { MAX_HARDCAP_SIZE } from '../../social-pixi-canvas/group-manager';
@Component({
  selector: 'app-presentation-toolbar',
  templateUrl: './presentation-toolbar.component.html',
  styleUrls: ['./presentation-toolbar.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PresentationToolBarComponent extends Loggable implements OnInit, OnDestroy, AfterViewInit {
  @Input() public showCompanyText = true;
  @Input() public showZoomControls = true;
  @Input() public social?: Social;
  @Input() public isPerforming?: boolean;
  @Input() protected isLoadingPresentation = true;
  @Input() public isOrientationExperience?: boolean;
  @Input() public isCountdownPage?: boolean;

  @ViewChild('emojiPicker', { static: false }) private emojiPickerElement: ElementRef | undefined;
  @ViewChild('emojiPickerButton', { static: false }) private emojiPickerButtonElement: ElementRef | undefined;

  @ViewChildren(PresentationMenuDropdownComponent)
  private presentationDropdowns?: QueryList<PresentationMenuDropdownComponent>;

  public ended: Subject<boolean> = new Subject<boolean>();

  public disableQAButton = true;
  public disableSaySomethingButton = false;
  public interactionEnabled = false;
  public isCameraDisabled = false;
  public isHost = false;

  public isMicEnabled = signal(true);
  public isPremiumPlaylist = false;
  public isSayingSomething = false;
  public isUserPresenting = false;
  public isVideoEnabled = true;
  public isVolumeControlVisible = false;
  public maxSliderVolume = 10;
  public playlist?: Playlist;
  public presenterName?: string;
  public screenShareActive = false;
  public showChat = true;
  public showQAButton = false;
  public showSaySomethingButton = false;
  public showZoom = true;
  public sliderVolumeLevel = INITIAL_MUSIC_VOLUME;
  protected musicVolume!: number;
  public someoneIsPerforming = false;
  public volumeControlOpen = true;
  public areCamerasAvailable = signal(false);
  public isCameraUnreadable = false;
  public allowDebugger = false;
  public disableCaptureDebugData = false;
  protected isChatOpen = false;
  public isDropdownOpen = false;
  public isHandRaised = false;
  public isHelpModalOpen = false;
  public inSocial = false; // flag to indicate we are joined to the social
  public presentersCount = 0;
  protected isSearchEnabled = false;

  public showClosedCaptionsButton = false;

  protected isSearchOpen = false;

  protected isPresentationActive = false;
  protected screenShareStatus = ScreenShareStatus.NONE;
  protected user?: User;
  protected isUserSharingScreen?: boolean;
  protected isPresentationRecording = false;
  protected isWideRecording = false;
  protected openMiniMap?: boolean;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private isCrossRoom = false;
  private isMixEnabled = false; // Hides Speak to Room button when cross room and mix is not enabled
  private raisedHandCount = 0;
  protected sliderVolumeLevelPreviousToClose = INITIAL_MUSIC_VOLUME;
  private raisedHandLimitHelper = 9; // Number used to limit raised hand counter text when the counter exceeds this default value
  protected presentationMenuType = PresentationMenuType;
  public isPresentationFull = false;

  protected defaultEmojis: string[] = [];

  // Group Share
  public isUserSharingToGroup = false;
  protected isGroupShareRecording = false;

  // Action Menu Options
  public canStartSocialEvent = true;

  //Social Settings
  public allowPresenter = true;
  public allowAnyonePresent = false;
  public allowAnyoneShareScreen = false;
  public showPresentation = true;
  protected displayScreenShareMessage = false;

  private isPopout?: PopOutState;
  protected shareScreenMenu = false;
  protected enhancedScreenShare = false;
  protected emotes = false;
  protected emotesPickerOpen = false;
  protected activeMediaEmbed = false;
  private soundControl = SoundControl.getInstance(this.sceneService);
  protected get canScreenShare() {
    return this.allowAnyoneShareScreen || this.isHost || this.isUserPresenting;
  }
  protected shareType = ShareType;

  //Group Info
  protected groupSoftCap = false;
  public player: Player | undefined;
  private groupMembers: Player[] = [];
  protected groupSoftCapValue = 0;
  protected groupHardCapValue = 0;
  protected groupParticipantsCount = 0;
  protected groupParticipantDefaultCount = 0;
  protected groupExtraParticipantsCount = 0;
  protected groupSpotsLeft = 0;
  protected groupFull = false;

  // SAG
  protected specialAccessGroups = false;
  private sagModal: SpecialAccessGroupModalState = { mode: SpecialAccessGroupModalMode.REQUEST_ACCESS, open: false };
  protected isMyUserInSag = false;

  // AI Summaries
  protected allowAiSummaries = false;

  public constructor(
    public sceneService: SceneService,
    private dialog: MatDialog,
    private chatService: ChatService,
    public deviceService: DeviceService,
    public playlistService: PlaylistService,
    public shareScreenService: ShareScreenService,
    public shareScreenParticipantService: ShareScreenParticipantService,
    private groupShareScreenRecorderService: GroupShareScreenRecorderService,
    public presentationRecorderService: PresentationRecorderService,
    private multimediaUserService: MultimediaUserService,
    private multimediaRoomService: MultimediaRoomService,
    private store: Store,
    private presentationService: PresentationService,
    private actionMenuService: ActionMenuService,
    public sideSectionService: SideSectionService,
    // private churnZeroService: ChurnZeroService,
    private debuggerService: DebuggerService,
    private changeDetectorRef: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private searchParticipantsService: SearchParticipantsService,
    private devicePermissionService: DevicePermissionService,
    private closedCaptionsService: ClosedCaptionsService,
    // private speechToTextService: SpeechToTextService,
    private emoteService: EmoteService,
  ) {
    super();
    this.setDisplayName('PresentationToolBarComponent');

    this.defaultEmojis = DefaultEmojis;

    // Subscribe to Music Volume Change
    this.soundControl.musicVolumeChanged$.pipe(takeUntil(this.destroyed$)).subscribe((newVolumeLevel) => {
      if (this.someoneIsPerforming && this.isPresentationActive) {
        return;
      }
      if (newVolumeLevel > 0) {
        this.musicVolume = newVolumeLevel;
      }

      // open / close Volume Control depending of volume
      if (this.sliderVolumeLevelPreviousToClose !== this.sliderVolumeLevel) {
        this.sliderVolumeLevelPreviousToClose = this.sliderVolumeLevel;
      }

      this.volumeControlOpen = newVolumeLevel > 0;
      this.sliderVolumeLevel = newVolumeLevel;
    });

    // Subscribe to Playlist Change
    this.playlistService.playlistChanged$.pipe(takeUntil(this.destroyed$)).subscribe((playlist) => {
      this.playlist = playlist;
      this.isPremiumPlaylist = this.playlist.type === 1;
      this.changeDetectorRef.markForCheck();
    });

    this.store
      .select(socialAppSelectors.getPresenterDetails)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroyed$))
      .subscribe((presenters: ExtendedPresenter[]) => {
        const mediaUser = presenters.find((slot: ExtendedPresenter) => slot.media);
        this.activeMediaEmbed = !!mediaUser?.media;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getRaisedHandCount)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((raisedHandCount) => {
        this.raisedHandCount = raisedHandCount;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getPresenterIds)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentersIds) => {
        this.presentersCount = presentersIds.length;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        if (!this.isPresentationActive && isPresentationActive) {
          this.showPresentationTooltip();
        }
        this.isPresentationActive = isPresentationActive;
        this.changeDetectorRef.markForCheck();
      });
    this.store
      .select(socialAppSelectors.isPresentationFull)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationFull) => {
        this.isPresentationFull = isPresentationFull;
        this.changeDetectorRef.markForCheck();
      });
    this.store
      .select(socialAppSelectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((active) => {
        this.isUserSharingToGroup = active;
        this.changeDetectorRef.markForCheck();
      });
    combineLatest([
      this.store.select(socialAppSelectors.isHost),
      this.sceneService.isFeatureAllowed(PRESENT),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_PRESENT),
      this.sceneService.isFeatureAllowed(ANYONE_CAN_SHARE),
      this.sceneService.isFeatureAllowed(AI_SUMMARIES),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([isHost, allowPresenter, allowAnyonePresent, allowAnyoneShareScreen, allowAiSummaries]) => {
        this.isHost = isHost ?? false;
        this.allowPresenter = allowPresenter;
        this.allowAnyonePresent = allowAnyonePresent;
        this.allowAnyoneShareScreen = allowAnyoneShareScreen;
        this.allowAiSummaries = allowAiSummaries;
        this.changeDetectorRef.markForCheck();
      });
    this.store
      .select(socialAppSelectors.isPresentationRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isRecording) => {
        if (isRecording !== this.isPresentationRecording) {
          this.isPresentationRecording = isRecording;
          this.changeDetectorRef.markForCheck();
          if (
            this.isPresentationRecording &&
            this.presentationRecorderService.recordingAuthorId !== this.user?.userId
          ) {
            this.openRecordingWarning('This presentation is being recorded.', 'success', 'START_RECORDING_3');
            this.changeDetectorRef.markForCheck();
          }
        }
      });

    this.store
      .select(socialAppSelectors.isPopOut)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPopOut) => {
        this.isPopout = isPopOut;
        this.changeDetectorRef.markForCheck();
      });

    this.groupShareScreenRecorderService.isGroupShareRecording$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isGroupShareRecording) => {
        if (this.isGroupShareRecording !== isGroupShareRecording) {
          this.isGroupShareRecording = isGroupShareRecording;
          this.changeDetectorRef.markForCheck();
          if (!this.isHost && this.isGroupShareRecording) {
            this.openRecordingWarning('This Group screen share is being recorded.', 'success', 'START_RECORDING_3');
            this.changeDetectorRef.markForCheck();
          }
        }
      });
    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_chat)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showChat) => {
        this.showChat = showChat;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_zoom)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showZoom) => {
        this.showZoom = showZoom;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_audio_mix)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((audioMixEnabled) => {
        this.isMixEnabled = audioMixEnabled;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(store, LaunchDarklyFlags.social_debugger)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((allowDebugger) => {
        this.allowDebugger = allowDebugger;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.search_v1)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isSearchEnabled) => {
        this.isSearchEnabled = isSearchEnabled;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.closed_captions)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showClosedCaptionsButton) => {
        if (showClosedCaptionsButton) {
          this.showClosedCaptionsButton = showClosedCaptionsButton;
          this.changeDetectorRef.markForCheck();
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enhanced_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enhanced_screen_share) => {
        this.enhancedScreenShare = enhanced_screen_share;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.emotes)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((emotes) => {
        this.emotes = emotes;
        this.changeDetectorRef.markForCheck();
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.group_soft_cap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupSoftCap) => {
        this.groupSoftCap = groupSoftCap;
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.special_access_groups)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((specialAccessGroups) => {
        this.specialAccessGroups = specialAccessGroups;
      });

    this.sideSectionService.setOpenState(SideContainerType.NONE);

    fromEvent(document, 'click')
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event) => {
        this.windowClick(event);
        this.changeDetectorRef.markForCheck();
      });
  }

  public get MultimediaType(): typeof MultimediaType {
    return MultimediaType;
  }

  public ngOnInit(): void {
    this.store
      .select(coreSelectors.getUserEntity)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getScreenShareState)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(({ status }) => {
        this.screenShareActive = status === ScreenShareStatus.ACTIVE;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isUserSharingScreen)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserSharingScreen) => {
        this.isUserSharingScreen = isUserSharingScreen;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(mediaSelectors.isVideoEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.isVideoEnabled = enabled;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(mediaSelectors.isCameraDisabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((disabled) => {
        this.isCameraDisabled = disabled;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(mediaSelectors.areAllCamerasUnreadable)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((areAllCamerasUnreadable) => this.areCamerasAvailable.set(!areAllCamerasUnreadable));

    // Detect an unreadable camera and show an error modal.
    // Skip the initial value in case the user joined with an unreadable camera. The modal should not be shown then
    this.store
      .select(mediaSelectors.isCurrentCameraReadable)
      .pipe(
        tap((readable) => (this.isCameraUnreadable = !readable)),
        skip(1),
        distinctUntilChanged(),
        switchMap((readable) => {
          if (readable) {
            return of(undefined);
          }
          const url = HelpCenterUrls.SCOOT_ENABLE_CAMERA_AND_MIC;
          const data: ModalErrorData = {
            title: CAMERA_UNREADABLE_TITLE,
            text: CAMERA_UNREADABLE_TEXT_IN_ROOM,
            helpLink: {
              url: url,
              text: CAMERA_UNREADABLE_HELP_TEXT,
            },
          };

          return this.dialog
            .open<ModalErrorComponent, ModalErrorData>(ModalErrorComponent, {
              data,
            })
            .afterClosed();
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(mediaSelectors.isMicEnabled)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.isMicEnabled.set(enabled);
      });

    this.store
      .select(socialAppSelectors.isUserPresenting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserPresenting) => {
        this.isUserPresenting = isUserPresenting;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getPresentationSettings)
      .pipe(
        filter((settings) => notNil(settings)),
        takeUntil(this.destroyed$),
      )
      .subscribe((settings) => {
        this.interactionEnabled = !!(settings && settings.interactionMode !== InteractionMode.NONE);
        this.changeDetectorRef.markForCheck();
      });
    this.actionMenuService.canStartSocialEvent$.pipe(takeUntil(this.destroyed$)).subscribe((canStart) => {
      this.canStartSocialEvent = canStart;
      this.changeDetectorRef.markForCheck();
    });
    this.store
      .select(socialAppSelectors.isCrossRoomPresentation)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isCrossRoom) => {
        this.isCrossRoom = isCrossRoom;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isJoined)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isJoined) => {
        this.inSocial = isJoined;
        this.changeDetectorRef.markForCheck();
      });

    this.sliderVolumeLevel = this.soundControl.musicVolume;
    this.multimediaUserService.canPushToTalk$.pipe(takeUntil(this.destroyed$)).subscribe((canPushToTalk) => {
      this.disableSaySomethingButton = !canPushToTalk;
      this.changeDetectorRef.markForCheck();
    });
    this.multimediaUserService.canRaiseHand$.pipe(takeUntil(this.destroyed$)).subscribe((canRaiseHand) => {
      this.disableQAButton = !canRaiseHand;
      this.changeDetectorRef.markForCheck();
    });
    this.multimediaUserService.isSayingSomething$.pipe(takeUntil(this.destroyed$)).subscribe((isSayingSomething) => {
      this.isSayingSomething = isSayingSomething;
      this.changeDetectorRef.markForCheck();
    });
    this.multimediaUserService.isHandRaised$.pipe(takeUntil(this.destroyed$)).subscribe((isHandRaised) => {
      this.isHandRaised = isHandRaised;
      this.changeDetectorRef.markForCheck();
    });
    this.subscribeToState();

    this.debuggerService.disableCaptureMedia?.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (result: boolean) => {
        this.disableCaptureDebugData = result;
        this.changeDetectorRef.markForCheck();
      },
    });

    this.store
      .select(socialAppSelectors.isWideRecording)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isWideRecording) => {
        this.isWideRecording = isWideRecording;
        this.changeDetectorRef.markForCheck();
      });

    this.sceneService.primaryGroupMemberSubject
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.destroyed$))
      .subscribe((groupMembers) => {
        if (this.groupSoftCap) {
          this.groupMembers = groupMembers;
          this.updateGroupInfo();
        }
      });

    this.store
      .select(socialAppSelectors.toggleMapOnboardingExperience)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((toggleMapOnboardingExperience) => {
        this.openMiniMap = toggleMapOnboardingExperience;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.specialAccessGroupModal)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((sagModal) => {
        this.sagModal = sagModal;
      });

    this.sceneService.sceneManager?.sagMap$?.pipe(takeUntil(this.destroyed$)).subscribe((sagMap) => {
      const groupId = this.sceneService.sceneManager?.player?.groupId;
      if (!groupId) {
        this.error('GroupId is not defined');
        return;
      }
      const usersGroup = sagMap.get(groupId);
      this.isMyUserInSag = !!usersGroup?.admitted.find((participant) => participant.userId === this.user?.userId);
      this.changeDetectorRef.markForCheck();
    });
  }

  public ngAfterViewInit(): void {
    this.store
      .select(socialAppSelectors.isChatEnabled)
      .pipe(distinctUntilChanged(), delay(0), takeUntil(this.destroyed$))
      .subscribe((enabled) => {
        this.isChatOpen = enabled;
        this.changeDetectorRef.markForCheck();
      });
  }

  private windowClick(event: Event) {
    if (this.emotesPickerOpen) {
      const clickedInside = this.emojiPickerElement?.nativeElement.contains(event.target);
      const clickedInsideButton = this.emojiPickerButtonElement?.nativeElement.contains(event.target);

      if (!clickedInside && !clickedInsideButton) {
        event.preventDefault();
        event.stopPropagation();
        this.closeEmojiPicker();
      }
    }
  }

  public get micButtonAvailableAndEnabled(): boolean {
    return this.isMicEnabled() && this.audioPermissionGranted;
  }

  public get videoButtonAvailableAndEnabled(): boolean {
    return this.isVideoEnabled && this.videoPermissionGranted;
  }

  public get raiseHandButtonText(): string {
    if (this.isSayingSomething) {
      return 'Stop Speaking';
    }
    return this.isHandRaised ? 'Lower Hand ' : 'Raise Hand';
  }

  public get raiseHandButtonTooltip(): string {
    if (this.isSayingSomething) {
      return 'Stop Speaking';
    }
    return this.isHandRaised ? 'Lower Hand ' : 'Raise your hand to ask a question.';
  }

  public get isInteractionDisabled(): boolean {
    return this.isPresentationActive && !this.interactionEnabled && !this.isHost && !this.isUserPresenting;
  }

  private subscribeToState(): void {
    combineLatest([this.store.select(getPresenterIds), this.store.select(socialAppSelectors.isPushToTalkActive)])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([presenterIds, isPushToTalkActive]) => {
        assertDefined(this.user?.userId);
        this.isUserPresenting = presenterIds.includes(this.user.userId);
        this.showSaySomethingButton =
          isPushToTalkActive &&
          !this.isUserPresenting &&
          ((this.isCrossRoom && this.isMixEnabled) || !this.isCrossRoom);
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.isQueuedQAndAActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isQueuedQAndAActive) => {
        this.showQAButton = isQueuedQAndAActive && !this.isUserPresenting;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getPresentation)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previousState, currentState]) => {
        // Presentation starting
        if (currentState && !previousState) {
          this.someoneIsPerforming = true;
          this.sliderVolumeLevel = this.soundControl.eventHostVolume;
          this.maxSliderVolume = 100;
        }

        // Presentation ending
        if (previousState && !currentState) {
          this.soundControl.musicVolume = this.musicVolume;
          this.sliderVolumeLevel = this.musicVolume;
          this.soundControl.setMusicVolume(this.musicVolume);
          this.someoneIsPerforming = false;
          this.maxSliderVolume = 10;
        }
        this.volumeControlOpen = this.sliderVolumeLevel > 0;
        this.changeDetectorRef.markForCheck();
      });

    this.store
      .select(socialAppSelectors.getPresentersNames)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presenterName) => {
        this.presenterName = presenterName;
        this.changeDetectorRef.markForCheck();
      });

    this.volumeControlOpen = this.sliderVolumeLevel > 0;
  }

  public async saySomething(): Promise<void> {
    this.logUserAction('Multimedia Start Speaking to Room', 'clicked');
    try {
      await this.multimediaUserService.handleRequestPushToTalk();
    } catch (e) {
      this.error('Error when pushing speak to room', e);
    }
  }

  public async stopSayingSomething(): Promise<void> {
    this.logUserAction('Multimedia Stop Speaking to Room', 'clicked');
    try {
      await this.multimediaUserService.handleRequestEndPushToTalk();
    } catch (e) {
      this.error('Error when pushing stop speaking to room', e);
    }
  }

  public async stopPresenting(): Promise<void> {
    this.logUserAction('Multimedia Stop presenting', 'clicked');
    if (this.isPopout?.open && document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    }
    try {
      await this.presentationService.stopPresenting();
    } catch (e) {
      this.error('Error when pushing stop presenting', 'clicked');
    }
  }

  public async handleRequestLowerHand(): Promise<void> {
    this.logUserAction('Request Lower Hand', 'clicked');
    try {
      await this.multimediaUserService.handleRequestLowerHand();
      this.changeDetectorRef.markForCheck();
    } catch (e) {
      this.error('Error when pushing lower hand', 'clicked');
    }
  }

  public async handleRequestRaiseHand(): Promise<void> {
    this.logUserAction('Request Raise Hand', 'clicked');
    try {
      await this.multimediaUserService.handleRequestRaiseHand();
      this.changeDetectorRef.markForCheck();
    } catch (e) {
      this.error('Error when pushing raise hand', 'clicked');
    }
  }

  public async handleQAButtonClick(): Promise<void> {
    this.logUserAction('Multimedia QA Button', 'clicked');
    if (this.isSayingSomething) {
      try {
        await this.stopSayingSomething();
        this.changeDetectorRef.markForCheck();
      } catch (e) {
        this.error('Error when stopping speaking to room', e);
      }
      return;
    }
    if (this.isHandRaised) {
      try {
        await this.handleRequestLowerHand();
        this.changeDetectorRef.markForCheck();
      } catch (e) {
        this.error('Error when requesting to lower hand', e);
      }
      return;
    }

    try {
      await this.handleRequestRaiseHand();
      this.changeDetectorRef.markForCheck();
    } catch (e) {
      this.error('Error when requesting to raise hand', e);
    }
  }

  public clickedToggleVideo(): void {
    this.logUserAction('Multimedia toggle Video', 'clicked');
    if (!this.videoPermissionGranted) {
      this.devicePermissionService.showExplanationModal(this.videoPermissionGranted, this.audioPermissionGranted);
    } else {
      this.multimediaUserService.toggleVideo();
    }
  }

  public clickedToggleMic(): void {
    this.logUserAction('Multimedia Toggle Mic', 'clicked');
    if (!this.audioPermissionGranted) {
      this.devicePermissionService.showExplanationModal(this.videoPermissionGranted, this.audioPermissionGranted);
    } else {
      this.multimediaUserService.userToggleAudio();
    }
  }

  public get unreadChatCount(): number {
    const { unreadMessagesCount } = this.chatService;
    return this.isUserPresenting ? unreadMessagesCount + this.raisedHandCount : unreadMessagesCount;
  }

  public badgeHidden(): boolean {
    return this.unreadChatCount <= 0;
  }

  public getBadgeCountDisplay(): string {
    const count = this.unreadChatCount;
    return count > this.raisedHandLimitHelper ? `${this.raisedHandLimitHelper}+` : count.toString();
  }

  // Open and Close Chat menu
  public toggleChat(): void {
    this.logUserAction('Multimedia Chat Button', `toggled ${this.isChatOpen ? 'closed' : 'opened'}`);

    if (this.isChatOpen) {
      this.sideSectionService.popSubMenu();
      this.store.dispatch(socialAppActions.TOGGLE_CHAT({ enabled: false }));
    } else {
      this.sideSectionService.openSubMenu(CHAT_SUB_MENU);
      this.store.dispatch(socialAppActions.TOGGLE_CHAT({ enabled: true }));
    }
    this.chatService.resetWindow();
  }

  protected addEmoji(emojiEvent: { emoji: Emoticon }): void {
    this.closeEmojiPicker();
    this.emoteService.setReactionMessage(emojiEvent.emoji);
  }

  protected toggleEmotePicker(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.emotesPickerOpen = !this.emotesPickerOpen;
  }

  protected closeEmojiPicker(): void {
    this.emotesPickerOpen = false;
  }

  public toggleMenu(): void {
    this.logUserAction("'Multimedia Toggle Menu", 'clicked');
    this.actionMenuService.toggleMenu();
  }

  public toggleVolumeControl(): void {
    this.logUserAction("'Multimedia Toggle Volume Control", 'clicked');
    this.isVolumeControlVisible = !this.isVolumeControlVisible;
  }

  public async clickedToggleMusic(): Promise<void> {
    this.logUserAction("'Multimedia Toggle Music", 'clicked');
    // Closing
    if (this.volumeControlOpen) {
      this.sliderVolumeLevelPreviousToClose = this.sliderVolumeLevel;
      try {
        await this.sliderDidChange({ value: 0 });
      } catch (e) {
        this.error('Error when slide changed', e);
      }
      // This component is subscribed to SoundControl.musicVolumeChanged (check constructor)
      // that will close Volume Control except when someone is Performing, the following line is backup for that
      this.volumeControlOpen = false;

      // Opening
    } else {
      try {
        await this.sliderDidChange({ value: this.sliderVolumeLevelPreviousToClose });
      } catch (e) {
        this.error('Error when slide changed', e);
      }
      // This component is subscribed to SoundControl.musicVolumeChanged (check constructor)
      // that will open Volume Control except when someone is Performing, the following line is backup for that
      this.volumeControlOpen = true;
    }
  }

  public sliderClick(e: MouseEvent | { value: number | null }): void {
    const { value } = e as { value: number | null };
    this.blurSliderAfterChange({ value });
  }

  public blurSliderAfterChange(e: { value: number | null }): void {
    document.getElementById('slider')?.blur();
  }

  public async sliderDidChange({ value }: { value: number | null }): Promise<void> {
    const { state } = SocialStateMachine.shared;
    const volume = value ?? this.sliderVolumeLevel;

    if (!state) {
      return;
    }

    if (this.isPresentationActive) {
      this.store.dispatch(socialAppActions.SET_MIX_VOLUME({ volume }));
    }

    if (this.isUserPresenting) {
      this.sliderVolumeLevel = volume;
      await this.multimediaRoomService.setCrowdVolume(volume ?? this.sliderVolumeLevel);
      return;
    }
    if (SocialStateHelper.isEventHappening(state, SocialEventType.PRESENTATION)) {
      this.sliderVolumeLevel = volume;
      this.soundControl.eventHostVolume = volume;
      const presenters = SocialStateHelper.getPresenterIds(state);
      presenters.forEach((p) => {
        const presenterPlayer = this.sceneService?.sceneManager?.getParticipant(p);
        if (!presenterPlayer) {
          return;
        }
        this.soundControl.setEventSpeakerVolume({
          p: presenterPlayer,
          newEventHostVolume: volume,
        });
      });

      // Playlist Volume
    } else {
      this.musicVolume = volume;
      this.soundControl.setMusicVolume(volume);
      // This component is subscribed to SoundControl.musicVolumeChanged (check constructor)
      // that will update the value of this.sliderVolumeLevel
    }
  }

  public async clickedNextSong(): Promise<void> {
    this.logUserAction('Multimedia Next Song', 'clicked');
    try {
      await this.playlistService.nextTrack();
    } catch (e) {
      this.error('Error when clicking next song', e);
    }
  }

  public openHelpModal(): void {
    this.logUserAction('Multimedia Open Help Modal', 'clicked');
    this.isHelpModalOpen = true;
    const dialogRef = this.dialog.open<HelpModalComponent>(HelpModalComponent, {
      width: '652px',
      panelClass: 'no-padding-social-modal',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.isHelpModalOpen = false;
        this.changeDetectorRef.markForCheck();
      });
  }

  public get cameraTooltip(): string {
    if (!this.videoPermissionGranted) {
      return 'Camera access permission denied';
    }
    if (this.isCameraDisabled) {
      return 'No cameras are available';
    }
    if (this.isCameraUnreadable) {
      return 'The selected camera is unavailable';
    }
    if (!this.videoButtonAvailableAndEnabled) {
      return 'Turn on camera';
    } else {
      return 'Turn off camera';
    }
  }

  public get audioTooltip(): string {
    if (this.isInteractionDisabled) {
      return 'Audience microphones have been disabled by the current presenter settings';
    }
    if (!this.audioPermissionGranted) {
      return 'Microphone access permission denied';
    }
    if (!this.micButtonAvailableAndEnabled) {
      return 'Turn on microphone';
    } else {
      return 'Turn off microphone';
    }
  }

  public async captureDebugData(): Promise<void> {
    this.logUserAction('Capture Debug Data', 'clicked');
    await this.debuggerService.captureDebugData();
  }
  public showDropdownEvent(event: boolean): void {
    //if any of the children's dropdown menus is open, add a class to the top element to change its z-index
    //and prevent it from being behind the 'Capture Debug Data' button. Otherwise, this component must stay behind said button.
    this.isDropdownOpen = event;
  }

  public closeOtherDropdowns(menuType: PresentationMenuType): void {
    this.presentationDropdowns?.forEach((dropdown) => {
      if (dropdown.type !== menuType) {
        dropdown.showDropdown = false;
      }
    });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    // Clear element references
    this.emojiPickerElement = undefined;
    this.emojiPickerButtonElement = undefined;
    this.user = undefined;
    this.social = undefined;
    this.playlist = undefined;
    this.presenterName = undefined;
  }

  public get sideContainerType(): typeof SideContainerType {
    return SideContainerType;
  }

  public async handleStopScreenShare(): Promise<void> {
    this.logUserAction('stop screen share', 'clicked');

    // end media embed if appropriate
    if (this.activeMediaEmbed) {
      await this.presentationService.updatePresentationMediaState(undefined);
      return;
    }

    if (this.isPopout?.open || document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    }

    await this.shareScreenService.stopSharing();
  }

  protected handleRecordGroupSharing(): void {
    this.groupShareScreenRecorderService.handleRecordGroupShare();
  }
  public get userCanScreenShare(): boolean {
    return this.isUserPresenting && (this.isHost || this.allowAnyoneShareScreen);
  }

  // Track the churnZero events for Presentation, Screen Share, Group Screen Share
  // private trackChurnZeroEvent(type: string): void {
  //   const date = moment().format('MMMM Do YYYY, h:mm:ss a');
  //   const customFields: ChurnZeroCustomFields = { Present: date, 'Presentation Type': type };
  //   this.churnZeroService.trackEvent(ChurnZeroEvent.PRESENTATION, customFields);
  // }

  public endPresentation(): void {
    this.logUserAction("Host Ended someone's presentation", 'clicked');
    this.presentationService.endPresentation().catch((e) => {
      this.error('Error ending presentation', { e });
    });
  }

  protected async handleQuickJoinPresentation(): Promise<void> {
    this.logUserAction('quick join presentation', 'clicked');
    await this.presentationService.joinPresentation();
  }
  protected stopScreenSharing(): void {
    this.shareScreenService.stopSharing().catch((e) => {
      this.error('Error stopping screen share', { e });
    });
  }

  public showPresentationTooltip(): void {
    this.displayScreenShareMessage = true;
    window.setTimeout(() => {
      this.displayScreenShareMessage = false;
    }, 5000);
  }

  public async handleQuickScreenShare(shareType: ShareType): Promise<void> {
    if (shareType === ShareType.EMBED) {
      this.handleQuickMediaShare(false);
      return;
    }

    if (this.screenShareActive) {
      this.stopScreenSharing();
      return;
    }

    this.logUserAction('quick screen share', 'clicked');
    // Track Churn Zero Event Screen share
    // this.trackChurnZeroEvent(ChurnZeroPresentationType.SCREEN_SHARE);

    if (!this.user) {
      return;
    }

    const shareScreenSuccess = await this.shareScreenService.startSharing(shareType === ShareType.HDSCREEN);

    if (shareScreenSuccess) {
      if (!this.isPresentationActive) {
        await this.presentationService.startPresentation({
          selectedPresenters: [this.user],
        });
      }
    }
  }

  // TODO: pull out into a service, DRY up
  protected handleQuickMediaShare(stop = true): void {
    if (stop) {
      void this.presentationService.updatePresentationMediaState(undefined);
    } else {
      this.handleEmbedPresentation();
    }
  }

  public handleEmbedPresentation(crossroom = false) {
    const dialogRef = this.dialog.open(PresentationEmbedModalComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: { mediaEmbedType: MediaEmbedType; embedCode: string } | null) => {
        if (!result) {
          return;
        }

        if (!this.user) {
          return;
        }

        const userCount = this.sceneService.getUserCount();

        const presentationSettings =
          userCount > 25 ? InteractionModePresets.ALL_HANDS : InteractionModePresets.OPEN_AUDIENCE_PARTICIPATION;

        if (this.isPresentationActive) {
          void this.presentationService.updatePresentationMediaState({
            url: result.embedCode,
            playing: false,
            startTime: 0,
            playtingTime: 0,
          });
          return;
        }

        void this.presentationService.startPresentation({
          selectedPresenters: [this.user],
          settings: presentationSettings,
          isCrossRoom: crossroom,
          mediaEmbedType: result.mediaEmbedType,
          embedCode: result.embedCode,
        });
      });
  }

  private openRecordingWarning(message: string, type?: SnackBarType, icon?: string, duration = 6000) {
    const _data: SnackBarData = {
      message: message,
      type: type || '',
      icon: icon || '',
      mode: this.presentationService.isPresentationActive ? 'dark' : 'light',
    };
    this._snackBar.openFromComponent(SnackBarComponent, {
      duration: duration,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'custom-snackbar',
      data: _data,
    });
  }

  public toggleSearchRoom(): void {
    this.store.dispatch(socialAppActions.TOGGLE_SEARCH_PARTICIPANTS({ open: !this.searchParticipantIsOpen }));
  }

  public get searchParticipantIsOpen(): boolean {
    return this.searchParticipantsService.isSearchParticipantOpen;
  }

  public getTooltipText(): string {
    if (this.isPresentationFull) {
      return 'Cannot join presentation when full';
    }
    return 'Join Presentation';
  }

  public get audioPermissionGranted(): boolean {
    return this.devicePermissionService.permission?.audioGranted;
  }

  public get videoPermissionGranted(): boolean {
    return this.devicePermissionService.permission?.videoGranted;
  }

  public get isClosedCaptionsEnabled(): boolean {
    return this.closedCaptionsService.closedCaptionsEnabled;
  }

  public toggleClosedCaptions(): void {
    if (this.showClosedCaptionsButton) {
      this.closedCaptionsService.toggleClosedCaptions();
    }
  }

  private calcParticipantsGroupDefaultCount() {
    if (this.groupParticipantsCount > this.groupSoftCapValue) {
      return this.groupSoftCapValue;
    }
    return this.groupParticipantsCount;
  }

  private calcExtraSpots() {
    const extraSpots = Math.min(
      this.groupHardCapValue - this.groupSoftCapValue,
      this.groupParticipantsCount - this.groupSoftCapValue,
    );
    if (extraSpots !== null && extraSpots >= 0) {
      return extraSpots;
    }
    return 0;
  }

  private calcSpotsLeft() {
    const spotsLeft = this.groupHardCapValue - (this.groupParticipantsCount + this.groupExtraParticipantsCount);
    if (spotsLeft !== null && spotsLeft >= 0) {
      return spotsLeft;
    }
    return 0;
  }

  public updateGroupInfo(): void {
    this.player = this.sceneService.getPlayer();
    this.groupHardCapValue = this.sceneService.maxGroupSize
      ? Math.min(MAX_HARDCAP_SIZE, this.sceneService.maxGroupSize)
      : MAX_HARDCAP_SIZE;
    this.groupSoftCapValue = Math.floor(this.groupHardCapValue / 1.5);
    this.groupParticipantsCount = this.groupMembers.length;
    this.groupParticipantDefaultCount = this.calcParticipantsGroupDefaultCount();
    this.groupExtraParticipantsCount = this.calcExtraSpots();
    this.groupSpotsLeft = this.calcSpotsLeft();
    this.groupFull = this.groupParticipantsCount >= this.groupHardCapValue;
  }

  protected get isSagModalOpen() {
    return this.sagModal.open && this.sagModal.mode === SpecialAccessGroupModalMode.REQUEST_ACCESS;
  }

  protected get meetingRecordingTooltip(): string {
    if (this.isWideRecording && this.allowAiSummaries) {
      return 'Meeting wide transcription and recording is enabled. Recording cannot be turned off at this time.';
    }

    if (this.isWideRecording) {
      return 'Meeting wide recording in progress. Recording cannot be turned off at this time.';
    }

    return this.isPresentationRecording ? 'End Recording' : 'Record';
  }
}

export enum ShareType {
  SCREEN = 'screen',
  HDSCREEN = 'media',
  EMBED = 'embed',
}
